import React, { useEffect, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { Building, FileText, CheckCircle } from 'lucide-react';

const card = { background: 'rgba(30,41,59,0.3)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '1rem' };
const input = { width: '100%', padding: '0.75rem 1rem', borderRadius: '0.75rem', background: 'rgba(15,23,42,0.5)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', outline: 'none' };
const btn = { padding: '0.75rem 1.5rem', background: 'linear-gradient(to right, #10b981, #06b6d4)', color: '#020617', fontWeight: 600, borderRadius: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' };

export default function Settings() {
  const { settings, fetchSettings, saveSettings, loading, user, updateAdminProfile, changeAdminPassword } = useApp();
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(false);
  const [profileEmail, setProfileEmail] = useState('');
  const [profileMessage, setProfileMessage] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwMessage, setPwMessage] = useState('');

  const isStaff = user?.role === 'staff';

  useEffect(() => {
    if (!isStaff) fetchSettings();
  }, [fetchSettings, isStaff]);

  useEffect(() => {
    if (!isStaff && settings) setForm(settings);
  }, [settings, isStaff]);

  useEffect(() => {
    setProfileEmail(user?.email || '');
  }, [user]);

  const handleSave = async () => {
    try {
      await saveSettings(form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      alert('Error saving settings');
    }
  };

  const handleSaveProfile = async () => {
    setProfileMessage('');
    if (!profileEmail) return setProfileMessage('Email is required');
    try {
      await updateAdminProfile({ email: profileEmail });
      setProfileMessage('Profile updated successfully');
      setTimeout(() => setProfileMessage(''), 3000);
    } catch (err) {
      setProfileMessage(err.response?.data?.error || 'Failed to update profile');
    }
  };

  const handleChangePassword = async () => {
    setPwMessage('');
    if (!newPassword || newPassword.length < 6) return setPwMessage('New password must be at least 6 characters');
    if (newPassword !== confirmPassword) return setPwMessage('Passwords do not match');
    try {
      await changeAdminPassword(currentPassword, newPassword);
      setPwMessage('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPwMessage(''), 3000);
    } catch (err) {
      setPwMessage(err.response?.data?.error || 'Failed to change password');
    }
  };

  if (!isStaff && !form) return <div style={{ padding: '2rem', color: '#94a3b8' }}>Loading...</div>;

  return (
    <div style={{ padding: '2rem' }}>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '2rem' }}>Settings</h1>
      <div style={{ maxWidth: '48rem', display: 'flex', flexDirection: 'column', gap: '2rem' }}>
        <div style={{ ...card, padding: '1.25rem' }}>
          <h2 style={{ fontWeight: 600, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><Building style={{ width: '1.25rem', height: '1.25rem', color: '#34d399' }} />Profile</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>Email</label>
              <input value={profileEmail} onChange={e => setProfileEmail(e.target.value)} style={input} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <button onClick={handleSaveProfile} disabled={loading} style={{ ...btn, opacity: loading ? 0.5 : 1 }}>Save Profile</button>
            {profileMessage && <span style={{ color: profileMessage.includes('success') ? '#34d399' : '#f97316' }}>{profileMessage}</span>}
          </div>
        </div>

        {!isStaff && (
          <>
            <div style={{ ...card, padding: '1.5rem' }}>
              <h2 style={{ fontWeight: 600, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><Building style={{ width: '1.25rem', height: '1.25rem', color: '#34d399' }} />Company Info</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                {[['Company Name', 'companyName'], ['Email', 'email'], ['Phone', 'phone'], ['Website', 'website']].map(([label, key]) => (
                  <div key={key}><label style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>{label}</label><input value={form[key] || ''} onChange={e => setForm({ ...form, [key]: e.target.value })} style={input} /></div>
                ))}
                <div style={{ gridColumn: 'span 2' }}><label style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>Address</label><input value={form.address || ''} onChange={e => setForm({ ...form, address: e.target.value })} style={input} /></div>
              </div>
            </div>

            <div style={{ ...card, padding: '1.5rem' }}>
              <h2 style={{ fontWeight: 600, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><FileText style={{ width: '1.25rem', height: '1.25rem', color: '#22d3ee' }} />Invoice Settings</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                {[['Tax Rate (%)', 'taxRate', 'number'], ['Payment Terms (days)', 'paymentTerms', 'number'], ['Invoice Prefix', 'invoicePrefix', 'text']].map(([label, key, type]) => (
                  <div key={key}><label style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>{label}</label><input type={type} value={form[key] || ''} onChange={e => setForm({ ...form, [key]: type === 'number' ? parseFloat(e.target.value) : e.target.value })} style={input} /></div>
                ))}
              </div>
            </div>

            <div style={{ ...card, padding: '1.5rem' }}>
              <h2 style={{ fontWeight: 600, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><CheckCircle style={{ width: '1.25rem', height: '1.25rem', color: '#34d399' }} />Testimonials (Public Site)</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>Show Testimonials Section</label>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', color: '#cbd5e1' }}>
                    <input
                      type="checkbox"
                      checked={Boolean(form.showTestimonials)}
                      onChange={(e) => setForm({ ...form, showTestimonials: e.target.checked })}
                    />
                    Enable on website
                  </label>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>Minimum Reviews to Show</label>
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={form.minTestimonialsToShow || 3}
                    onChange={e => setForm({ ...form, minTestimonialsToShow: Math.max(1, parseInt(e.target.value || '3', 10)) })}
                    disabled={!Boolean(form.showTestimonials)}
                    style={{ ...input, opacity: Boolean(form.showTestimonials) ? 1 : 0.6 }}
                  />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <button onClick={handleSave} disabled={loading} style={{ ...btn, opacity: loading ? 0.5 : 1 }}><CheckCircle style={{ width: '1rem', height: '1rem' }} /> Save Settings</button>
              {saved && <span style={{ color: '#34d399' }}>Saved</span>}
            </div>
          </>
        )}

        <div style={{ ...card, padding: '1.25rem' }}>
          <h2 style={{ fontWeight: 600, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><FileText style={{ width: '1.25rem', height: '1.25rem', color: '#f97316' }} />Change Password</h2>
          <form onSubmit={(e) => { e.preventDefault(); handleChangePassword(); }} style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0.75rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <div><label style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>Current Password</label><input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} style={input} /></div>
              <div><label style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>New Password</label><input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} style={input} /></div>
              <div style={{ gridColumn: 'span 2' }}><label style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>Confirm New Password</label><input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} style={input} /></div>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <button type="submit" disabled={loading} style={{ ...btn, opacity: loading ? 0.5 : 1 }}>Change Password</button>
              {pwMessage && <span style={{ color: pwMessage.includes('success') ? '#34d399' : '#f97316' }}>{pwMessage}</span>}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
